import React from "react";

import { LazyQueryExecFunction } from "@apollo/client";
import {
  AnyFilterExpressionInput,
  ByProjectIdReqInput,
  Exact,
  MdmDeviceDto,
  MdmObjectDto,
  SortDirection,
  SortInput
} from "gql/generated/types";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import useCustomColumns from "../hooks/useCustomColumns";
import useProjectContext from "../hooks/useProjectContext";
import { initialFilter } from "../utils/const";
import { MdmContainerName, MdmTableName, TableColumn, TableOptions } from "../utils/types";
import { searchDeviceOrObjectFilter } from "../utils/utils";
import LazyTable from "./LazyTable";
import TableColumnSelection from "./TableColumnSelection";


type ObjectAndDeviceTableWrapperProps<T, N> = {
  data: T[];
  tableName: MdmTableName;
  containerName: MdmContainerName;
  columns: TableColumn<T>[];
  loading: boolean;
  setTableOptions: React.Dispatch<React.SetStateAction<TableOptions>>;
  tableOptions: TableOptions;
  totalItems: number;
  getQueryData: LazyQueryExecFunction<N, Exact<{ input: ByProjectIdReqInput }>>;
  getIsolatedData: LazyQueryExecFunction<N, Exact<{ input: ByProjectIdReqInput }>>;
};

const ObjectAndDeviceTableWrapper = <T extends NoInfer<MdmDeviceDto | MdmObjectDto>, N>({ 
  data,
  tableName,
  containerName,
  columns,
  loading,
  setTableOptions,
  tableOptions,
  totalItems,
  getQueryData,
  getIsolatedData,
 }: ObjectAndDeviceTableWrapperProps<T, N>) => {
  const { projectId } = useProjectContext();
  const navigate = useNavigate();
  const [
    customColumns,
    setCustomColumns,
    formattedColumns,
    mapCols,
    formatObjectFields,
    handleColumnSave,
    updateLoading,
  ] = useCustomColumns(tableName, columns, data, tableOptions.rows);

  const fetchData = async (
    take: number | null = null,
    skip: number | null = null,
    sort: SortInput[] | null = null,
    filters: AnyFilterExpressionInput[] | null = null,
    cacheAndNetwork: boolean = false,
    filter: AnyFilterExpressionInput | null = null,
    isolatedFetch: boolean = false,
  ) => {
    const fetchFn = isolatedFetch ? getIsolatedData : getQueryData;
    const resp = await fetchFn({
      variables: {
        input: {
          projectId,
          take: take ?? tableOptions.rows,
          skip: skip ?? tableOptions.first,
          sort: sort ?? [{
            property: tableOptions.sortField,
            direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
          }],
          andFilters: filters ?? tableOptions.andFilters,
          filter: filter ?? tableOptions.filter,
        },
      },
      fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
    });

    return resp.data
  };

  const handleSearch = (value: string) => {
    setTableOptions(prev => ({
      ...prev,
      filter: value ? searchDeviceOrObjectFilter(value, tableName) : null,
      first: 0,
      search: value,
    }));
  };

  const resetFilters = () => {
    setTableOptions(prev => ({
      ...initialFilter,
      rows: prev.rows,
      sortField: "identifier",
      reqSortField: "identifier",
      sortOrder: 1,
    }));
  };
  
  return (
    <LazyTable
      columns={formattedColumns}
      data={formatObjectFields}
      tableName={tableName}
      containerName={containerName}
      loading={loading}
      setTableOptions={setTableOptions}
      tableOptions={tableOptions}
      totalItems={totalItems}
      fetchData={fetchData}
      resetFilters={resetFilters}
      onSearch={handleSearch}
      setCustomColumns={setCustomColumns}
      buttons={
        <>
          <TableColumnSelection
            customColumns={customColumns}
            tableName={tableName}
            setCustomColumns={setCustomColumns}
            handleColumnSave={handleColumnSave}
            mapCol={mapCols}
            loading={updateLoading}
          />
          <Button
            type="button"
            icon="pi pi-plus"
            size="small"
            label={tableName === "Devices" ? T("mdm.add-device") : T("mdm.add-object")}
            onClick={() => navigate("new")}
          />
        </>
      }
    />
  );
}

export default ObjectAndDeviceTableWrapper;